import orderBy from 'lodash/orderBy';
import { action, computed, makeObservable, observable, override } from 'mobx';

import { AnyObject, SortingOrder } from '@store/types';
import {
    EntryDetailData,
    EntryListItem as EntryListResponseParams,
    EntryListQuery,
    IntervalWithAuthorQuery,
    Optional,
} from 'chaika-frontend-types/frontend-cms';
import { TranslationsDataParams } from 'chaika-types';

import { LongList } from '@store/abstract/LongList';
import { FileAsset } from '@store/models/FileAsset';

export interface EntryFilterParams {
    ehrIds?: string;
}

export interface EntryListParams extends EntryListResponseParams {
    entryTypeType: string;
    entryTypeTitle: string;
    entryTypeTitleTranslations?: TranslationsDataParams[];
    statusTitle?: string;
    indexNumber?: number;
    payedCount?: number;
    medicalCaseIds?: string[];
    needPaymentCount?: number;
}

export interface EntryFiles {
    entryId: string;
    files: FileAsset[];
}

const DEFAULT_FIELD = 'creationTime';

export class EntryLongList extends LongList<EntryListParams, EntryListParams, EntryFilterParams> {
    public readonly limit: number = 50;

    @observable public staticRestParams: Partial<IntervalWithAuthorQuery | EntryListQuery> = {};
    @observable public restParams: IntervalWithAuthorQuery | EntryListQuery = {};

    protected readonly paginationStep: number = 30;
    @observable protected paginationLimit: number = this.paginationStep;
    @observable protected entryDetails = new Map<string, EntryDetailData>();
    @observable protected entryFiles = new Map<string, FileAsset[]>();
    @observable private medicalCaseIds = new Map<string, string[]>();

    constructor(params?: EntryListParams[]) {
        super(params);
        makeObservable(this);

        this.orderParams = {
            fieldName: DEFAULT_FIELD,
            order: SortingOrder.DESC,
        };
    }

    @computed
    public get hasMore(): boolean {
        return this.paginationLimit < this.filteredValues.length;
    }

    @computed
    public get paginateFilteredValues(): EntryListParams[] {
        return this.filteredValues.slice(0, this.paginationLimit);
    }

    @override
    public get filteredValues(): EntryListParams[] {
        const { fieldName = DEFAULT_FIELD, order = SortingOrder.DESC } = this.getOrderParams();

        const valuesWithIndexNumbers = this.getValuesWithIndexNumbers(this.values);
        const filteredValuesWithIndexNumbers = valuesWithIndexNumbers.filter(value =>
            this.filterValue(value, this.filterParams),
        );

        return orderBy(
            filteredValuesWithIndexNumbers,
            value => {
                if (fieldName === DEFAULT_FIELD) {
                    return value.visitTime ?? value.creationTime;
                }
                return (value as AnyObject)[fieldName];
            },
            order,
        );
    }

    public get hasEntryDetails(): boolean {
        return !!this.entryDetails?.size;
    }

    @action
    public showMoreItems(): void {
        if (this.paginationLimit < this.getSize()) {
            this.paginationLimit += this.paginationStep;
        }
    }

    @action
    public showAllItems(): void {
        this.paginationLimit = this.getSize();
    }

    @override
    public setFilterParams(params: Partial<EntryFilterParams> = {}): void {
        this.filterParams = { ...this.filterParams, ...params };
    }

    @action
    public setEntryDetails(details: EntryDetailData[]): void {
        this.entryDetails = new Map(details.map(detail => [detail.entryId, detail]));
    }

    @action
    public setEntryFiles(entryFiles: EntryFiles[]): void {
        this.entryFiles = new Map(entryFiles.map(it => [it.entryId, it.files]));
    }

    @action
    public addIfNotExistsEntryDetails(details: EntryDetailData[]): void {
        const newEntryDetails = new Map(this.entryDetails);

        details.forEach(detail => {
            if (!newEntryDetails.has(detail.entryId)) {
                newEntryDetails.set(detail.entryId, detail);
            }
        });

        this.entryDetails = newEntryDetails;
    }

    @action
    public addIfNotExistEntryFiles(entryFiles: EntryFiles[]): void {
        const newEntryFiles = new Map(this.entryFiles);

        entryFiles.forEach(({ entryId, files }) => {
            const existingFiles = newEntryFiles.get(entryId) || [];
            const uniqueFiles = files.filter(file => !existingFiles.some(existing => existing.path === file.path));

            newEntryFiles.set(entryId, [...existingFiles, ...uniqueFiles]);
        });

        this.entryFiles = newEntryFiles;
    }

    @action
    public setMedicalCaseIds(entryId: string, medicalCaseIds: string[]): void {
        this.medicalCaseIds.set(entryId, medicalCaseIds);
    }

    @action
    public setRestParams(params: IntervalWithAuthorQuery | EntryListQuery): void {
        this.restParams = params;
    }

    public hasNotEmptyMedicalCaseIds(entryId: string): boolean {
        return !!this.medicalCaseIds.get(entryId)?.length;
    }

    public getEntryDetails(entryId: string): Optional<EntryDetailData> {
        return this.entryDetails.get(entryId);
    }

    public getEntryFiles(entryId: string): Optional<FileAsset[]> {
        return this.entryFiles.get(entryId);
    }

    protected create(params: EntryListParams): EntryListParams {
        return params;
    }

    protected getValuesWithIndexNumbers(values: EntryListParams[]): EntryListParams[] {
        return orderBy(values, value => value.visitTime ?? value.creationTime, SortingOrder.DESC).map(
            (value, index) => ({
                ...value,
                indexNumber: index + 1,
            }),
        );
    }

    protected filterValue(value: EntryListParams, { ehrIds }: EntryFilterParams): boolean {
        return this.filterArrayFieldValue(value, ehrIds, 'ehrId');
    }
}
